<script lang="ts">
  // Matrix mode per INTERFACE-SPECIFICATION.md §4.
  //
  // Two view depths: Surface (one table comparing all four products
  // through the marketing surface) and Cube (per-product tabs each
  // showing the canonical cube). Sub-tabs cycle through Rates by Band,
  // Fees, Purposes, Terms, Methodology (Governance sub-tab on a
  // per-product cube is the §10 Governance view, reached separately).
  //
  // Channel selector for Cube view; the surface view aggregates across
  // housing on the default hLoan channel.

  import { PRODUCTS, productKey } from '../lib/products';
  import { cachedCatalogue } from '../lib/catalogue-store.svelte';
  import { surfaceState } from '../lib/surface.svelte';
  import SurfaceMatrix from './SurfaceMatrix.svelte';
  import CubeTable from './CubeTable.svelte';
  import { formatAmount, formatRate } from '../lib/formatters';

  type SubTab = 'rates' | 'fees' | 'purposes' | 'terms' | 'methodology';

  interface Props {
    // Sub-tab name from the URL (/catalogue/matrix/<subtab>). Owned by
    // the route per INTERFACE-REVIEW.md §5 R6 so sub-tab state is
    // deep-linkable, back-button-friendly, and visible to anyone
    // reading the address bar (the screenshot legibility win).
    subTab: SubTab;
  }

  const { subTab }: Props = $props();

  // Cube view product selection — defaults to AU unsecured. Channel
  // selection sits next to the cube table (the only place channel
  // matters). Both remain local state today; URL-encoding them is
  // INTERFACE-REVIEW.md §10 question 3, deferred until shareable
  // "AU unsecured cube on hMoney" links are an actual workflow.
  let activeProductKey = $state<string>('au/unsecured-personal-loan');
  let activeChannel = $state<string>('hLoan');

  const activeProduct = $derived(
    PRODUCTS.find((p) => productKey(p) === activeProductKey) ?? PRODUCTS[0]!
  );
  const activeCatalogue = $derived(cachedCatalogue(activeProduct.jurisdiction, activeProduct.product));

  const subTabs: { key: SubTab; label: string }[] = [
    { key: 'rates', label: 'Rates by band' },
    { key: 'fees', label: 'Fees' },
    { key: 'purposes', label: 'Purposes' },
    { key: 'terms', label: 'Terms' },
    { key: 'methodology', label: 'Methodology' }
  ];

  function subTabHref(t: SubTab): string {
    return `/catalogue/matrix/${t}`;
  }
  function selectProductKey(k: string) { activeProductKey = k; }

  // Purposes table cross-product matrix:
  // start from the AU unsecured catalogue's purpose list (the union
  // is widest there) and look up presence in the other three.
  const auUnsecPurposes = $derived(cachedCatalogue('au', 'unsecured-personal-loan')?.loan_purposes ?? []);
</script>

<section>
  <nav class="sub-tabs" aria-label="Matrix view sections">
    {#each subTabs as t (t.key)}
      <a
        href={subTabHref(t.key)}
        class:is-active={subTab === t.key}
      >{t.label}</a>
    {/each}
  </nav>

  {#if subTab === 'rates'}
    {#if surfaceState.viewDepth === 'surface'}
      <SurfaceMatrix />
    {:else}
      <div class="cube-controls">
        <div class="toggle-group" role="group" aria-label="Active product">
          {#each PRODUCTS as p (p.jurisdiction + p.product)}
            <button
              type="button"
              class:is-active={productKey(p) === activeProductKey}
              onclick={() => selectProductKey(productKey(p))}
            >{p.label}</button>
          {/each}
        </div>
        <label>
          <span class="muted">Channel</span>
          <select bind:value={activeChannel}>
            <option value="hLoan">hLoan</option>
            <option value="hMoney">hMoney</option>
          </select>
        </label>
      </div>
      {#if activeCatalogue}
        <h3 class="section-heading">{activeProduct.label} — {activeProduct.cubeShapeLabel} · channel {activeChannel}</h3>
        <CubeTable catalogue={activeCatalogue} channel={activeChannel} />
      {:else}
        <p class="muted">Loading {activeProduct.label}…</p>
      {/if}
    {/if}
  {:else if subTab === 'fees'}
    <h3 class="section-heading">Establishment fees</h3>
    <table class="cube-table">
      <thead>
        <tr>
          <th>Tier</th>
          {#each PRODUCTS as p (p.jurisdiction + p.product)}
            <th class="num">$ — {p.label}</th>
          {/each}
        </tr>
      </thead>
      <tbody>
        {#each ['establishment_fee.tier_1.amount', 'establishment_fee.tier_2.amount'] as path (path)}
          <tr>
            <td><code>{path}</code></td>
            {#each PRODUCTS as p (p.jurisdiction + p.product)}
              {@const cube = cachedCatalogue(p.jurisdiction, p.product)}
              {@const segments = path.split('.')}
              {@const value = segments.reduce<unknown>((acc, k) => (acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[k] : undefined), cube?.fees ?? {})}
              <td class="num">{typeof value === 'number' ? formatAmount(value) : '—'}</td>
            {/each}
          </tr>
        {/each}
      </tbody>
    </table>
    <p class="muted">Fee data shown as authored. Detail/Edit (commit 5) allows staging fee changes via the catalogue-client's fee_edits path.</p>
  {:else if subTab === 'purposes'}
    <h3 class="section-heading">Loan purposes</h3>
    <table class="cube-table">
      <thead>
        <tr>
          <th>ID</th>
          <th>Display name</th>
          {#each PRODUCTS as p (p.jurisdiction + p.product)}
            <th>{p.label}</th>
          {/each}
        </tr>
      </thead>
      <tbody>
        {#each auUnsecPurposes as purpose (purpose.id)}
          <tr>
            <td><code>{purpose.id}</code></td>
            <td>{purpose.display_name}</td>
            {#each PRODUCTS as p (p.jurisdiction + p.product)}
              {@const otherCube = cachedCatalogue(p.jurisdiction, p.product)}
              {@const exists = otherCube?.loan_purposes?.some((q) => q.id === purpose.id)}
              <td class="muted">{exists ? '✓' : '—'}</td>
            {/each}
          </tr>
        {/each}
      </tbody>
    </table>
  {:else if subTab === 'terms'}
    <h3 class="section-heading">Loan terms and amount range</h3>
    <table class="cube-table">
      <thead>
        <tr>
          <th>Field</th>
          {#each PRODUCTS as p (p.jurisdiction + p.product)}
            <th class="num">— {p.label}</th>
          {/each}
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Amount range $ min</td>
          {#each PRODUCTS as p (p.jurisdiction + p.product)}
            {@const cube = cachedCatalogue(p.jurisdiction, p.product)}
            <td class="num">{cube ? formatAmount(cube.loan_amount_range.min) : '—'}</td>
          {/each}
        </tr>
        <tr>
          <td>Amount range $ max</td>
          {#each PRODUCTS as p (p.jurisdiction + p.product)}
            {@const cube = cachedCatalogue(p.jurisdiction, p.product)}
            <td class="num">{cube ? formatAmount(cube.loan_amount_range.max) : '—'}</td>
          {/each}
        </tr>
        <tr>
          <td>Increment $</td>
          {#each PRODUCTS as p (p.jurisdiction + p.product)}
            {@const cube = cachedCatalogue(p.jurisdiction, p.product)}
            <td class="num">{cube ? formatAmount(cube.loan_amount_range.increment) : '—'}</td>
          {/each}
        </tr>
        <tr>
          <td>Available terms (months)</td>
          {#each PRODUCTS as p (p.jurisdiction + p.product)}
            {@const cube = cachedCatalogue(p.jurisdiction, p.product)}
            <td class="muted">{cube?.terms_months.join(', ') ?? '—'}</td>
          {/each}
        </tr>
      </tbody>
    </table>
  {:else if subTab === 'methodology'}
    <h3 class="section-heading">Methodology</h3>
    <table class="cube-table">
      <thead>
        <tr>
          <th>Field</th>
          {#each PRODUCTS as p (p.jurisdiction + p.product)}
            <th>— {p.label}</th>
          {/each}
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Comparison rate required</td>
          {#each PRODUCTS as p (p.jurisdiction + p.product)}
            {@const cube = cachedCatalogue(p.jurisdiction, p.product)}
            <td>{cube ? (cube.product.comparison_rate_required ? 'Yes' : 'No') : '—'}</td>
          {/each}
        </tr>
        <tr>
          <td>Secured discount %</td>
          {#each PRODUCTS as p (p.jurisdiction + p.product)}
            {@const cube = cachedCatalogue(p.jurisdiction, p.product)}
            <td class="num">{cube?.product.secured_rate_discount_pct ? formatRate(cube.product.secured_rate_discount_pct) : '—'}</td>
          {/each}
        </tr>
        <tr>
          <td>Pricing model</td>
          {#each PRODUCTS as p (p.jurisdiction + p.product)}
            {@const cube = cachedCatalogue(p.jurisdiction, p.product)}
            <td class="muted"><code>{cube?._dimension_refs.pricing_model ?? '—'}</code></td>
          {/each}
        </tr>
        <tr>
          <td>Bureau default</td>
          {#each PRODUCTS as p (p.jurisdiction + p.product)}
            {@const cube = cachedCatalogue(p.jurisdiction, p.product)}
            <td class="muted">{cube?._jurisdiction_metadata.bureau_default ?? '—'}</td>
          {/each}
        </tr>
      </tbody>
    </table>
  {/if}
</section>

<style>
  .sub-tabs {
    display: flex;
    gap: var(--space-2);
    margin-bottom: var(--space-4);
    border-bottom: 1px solid var(--surface-line-soft);
  }
  .sub-tabs a {
    background: transparent;
    padding: var(--space-2) var(--space-3);
    font-size: var(--type-sm);
    color: var(--surface-muted);
    border-bottom: 2px solid transparent;
    text-decoration: none;
    cursor: pointer;
  }
  .sub-tabs a:hover { color: var(--surface-fg); }
  .sub-tabs a.is-active {
    color: var(--surface-fg);
    border-bottom-color: var(--surface-fg);
    font-weight: 500;
  }
  .cube-controls {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    margin-bottom: var(--space-3);
    flex-wrap: wrap;
  }
  code { font-family: var(--font-mono); }
</style>
